import { useCallback, useMemo, useState } from "react";
import {
  AgGrid,
  Button,
  DropdownButton,
  Flex,
  Grid,
  SegmentedControl,
  Text,
  toast,
} from "@jsq/ds/core";
import { useAppContext } from "~/contexts/appContext";
import {
  useDataPackageUsageQuery,
  useDuplicateDataPackageMutation,
  useGetDataPackagesQuery,
} from "~/graphql/supergraphHooks";
import { REPORTING_PAGE_SIZE } from "~/pages/Reporting/constants";
import { useDataStructureRoutingContext } from "~/pages/Reporting/DataStructureRoutingContext";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { hasGlobalReportingAccess } from "~/utils/userRoles";
import { DataPackageSlideout } from "./DataPackageSlideout";
import { DataPackageTakeover } from "./DataPackageTakeover";
import {
  DuplicateDataPackageModal,
  type DuplicateDataPackageFormValues,
} from "./DuplicateDataPackageModal";
import { NoRowsOverlay } from "./NoRowsOverlay";
import {
  compareByNameWithDateFallback,
  compareDateStrings,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
} from "./sorting";
import * as styles from "./styles_vanilla.css";
import { VIEW_FILTER_OPTIONS, type DataPackage, type ViewFilter } from "./types";
const GRID_OVERRIDES = {
  paginationPageSize: REPORTING_PAGE_SIZE,
  pagination: true,
  domLayout: "autoHeight" as const,
  rowHeight: 50,
  suppressHorizontalScroll: true,
  sideBar: false,
  statusBar: undefined,
  suppressLoadingOverlay: true,
};

const getArchivedByCellValue = (
  data: DataPackage | undefined,
  formatDateWithTimezone: (date: string, format: string, includeTz?: boolean) => string,
): string => {
  const name = data?.archivedByFullName?.trim();
  const date = data?.archivedAt;
  const formattedDate = date ? formatDateWithTimezone(date, DATE_FORMAT, false) : "";

  if (name && formattedDate) {
    return `${name} on ${formattedDate}`;
  }

  return name || formattedDate || "";
};

const getLastUpdatedCellValue = (
  data: DataPackage | undefined,
  formatDateWithTimezone: (date: string, format: string) => string,
): string => {
  const timestamp = data?.lastUpdated;
  const by = data?.updatedByFullName;
  const formatted = timestamp ? formatDateWithTimezone(timestamp, DATE_TIME_FORMAT) : "";
  return by ? `${formatted} by ${by}` : formatted;
};

const compareArchivedBy = (a?: DataPackage, b?: DataPackage): number => {
  return compareByNameWithDateFallback(
    a?.archivedByFullName,
    b?.archivedByFullName,
    a?.archivedAt,
    b?.archivedAt,
  );
};

export type DataPackagesListProps = {
  title: string;
  arenaId: number | null;
  arenaName?: string;
};

export const DataPackagesList = ({ title, arenaId, arenaName }: DataPackagesListProps) => {
  const { user } = useAppContext();
  const showGlobalSettingsLink = hasGlobalReportingAccess(user.roles);
  const {
    viewFilter,
    handleViewFilterChange: onViewFilterChange,
    dataPackageId,
    dataStructureType,
    isNewDataPackage,
    navigateToList,
    navigateToDataPackage,
    navigateToNewDataPackage,
  } = useDataStructureRoutingContext();
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const {
    data,
    loading,
    refetch: refetchDataPackages,
  } = useGetDataPackagesQuery({
    variables: { arenaId, includeInactive: true },
    fetchPolicy: "cache-and-network",
  });
  const [duplicateDataPackageMutation] = useDuplicateDataPackageMutation();

  const sourcePackages = useMemo(() => data?.finpubDataPackages ?? [], [data]);

  const dataPackageIds = useMemo(() => sourcePackages.map((pkg) => pkg.id), [sourcePackages]);

  const { data: usageData } = useDataPackageUsageQuery({
    variables: { dataPackageIds },
    skip: dataPackageIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const isUsageLoaded = !!usageData;

  const usageByPackageId = useMemo(() => {
    const map = new Map<number, number>();
    usageData?.dataPackageUsage?.forEach((u) => {
      map.set(u.dataPackageId, u.activeTemplateCount);
    });
    return map;
  }, [usageData]);
  const isArenaSpecificView = arenaId !== null;

  const scopedPackages = useMemo(
    () => sourcePackages.filter((pkg) => (arenaId === null ? pkg.arenaId === null : true)),
    [sourcePackages, arenaId],
  );

  const mapToDataPackage = useCallback(
    (pkg: (typeof scopedPackages)[number]): DataPackage => ({
      id: pkg.id,
      arenaId: pkg.arenaId,
      name: pkg.name,
      description: pkg.description ?? "",
      hierarchy: `${pkg.nodeCount} nodes, ${pkg.levelCount} levels`,
      formulas: pkg.formulaCount,
      queries: pkg.queryCount,
      usage: isUsageLoaded ? (usageByPackageId.get(pkg.id) ?? 0) : null,
      lastUpdated: pkg.lastUpdated ?? "",
      updatedByFullName: pkg.updatedByUser?.fullName?.trim() || null,
      archivedAt: pkg.archivedAt ?? null,
      archivedByFullName: pkg.archivedByUser?.fullName?.trim() || null,
      isDefault: pkg.isDefault,
    }),
    [usageByPackageId, isUsageLoaded],
  );

  const rowData: DataPackage[] = useMemo(() => {
    if (isArenaSpecificView) {
      return [];
    }

    return scopedPackages
      .filter((pkg) =>
        viewFilter === "active" ? pkg.isActive !== false : pkg.isActive === false,
      )
      .map(mapToDataPackage);
  }, [isArenaSpecificView, scopedPackages, viewFilter, mapToDataPackage]);

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateSourceId, setDuplicateSourceId] = useState<number | null>(null);
  const duplicateTargetArenaId = arenaId;

  const isSlideoutOpen = dataPackageId !== null;

  const systemRowData = useMemo(
    () =>
      scopedPackages
        .filter((pkg) => pkg.arenaId === null && pkg.isActive !== false)
        .map(mapToDataPackage),
    [scopedPackages, mapToDataPackage],
  );
  const arenaRowData = useMemo(
    () =>
      scopedPackages
        .filter((pkg) => pkg.arenaId !== null)
        .filter((pkg) =>
          viewFilter === "active" ? pkg.isActive !== false : pkg.isActive === false,
        )
        .map(mapToDataPackage),
    [scopedPackages, viewFilter, mapToDataPackage],
  );
  const visibleRowData = useMemo(
    () => (isArenaSpecificView ? [...systemRowData, ...arenaRowData] : rowData),
    [isArenaSpecificView, systemRowData, arenaRowData, rowData],
  );

  const selectedPackage = useMemo(() => {
    return visibleRowData.find((p) => p.id === dataPackageId) ?? null;
  }, [visibleRowData, dataPackageId]);

  const handleRowClick = useCallback(
    (event: AgGrid.RowClickedEvent<DataPackage>) => {
      if (event.data) {
        navigateToDataPackage(event.data.id);
      }
    },
    [navigateToDataPackage],
  );

  const handleCloseSlideout = useCallback(() => {
    navigateToList();
  }, [navigateToList]);

  const handleRefreshList = useCallback(() => {
    void refetchDataPackages();
  }, [refetchDataPackages]);

  const handleOpenCreateTakeover = useCallback(() => {
    navigateToNewDataPackage();
  }, [navigateToNewDataPackage]);

  const handleCloseCreateTakeover = useCallback(() => {
    navigateToList();
  }, [navigateToList]);

  const handleOpenDuplicateModal = useCallback(() => {
    setDuplicateSourceId(null);
    setIsDuplicateModalOpen(true);
  }, []);

  const handleOpenGlobalSettings = useCallback(() => {
    const url = `/canopy/reporting/settings/data-structures?type=${dataStructureType}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [dataStructureType]);

  const handleDuplicateFromSlideout = useCallback(() => {
    setDuplicateSourceId(dataPackageId);
    setIsDuplicateModalOpen(true);
  }, [dataPackageId]);

  const handleCloseDuplicateModal = useCallback(() => {
    setIsDuplicateModalOpen(false);
    setDuplicateSourceId(null);
  }, []);

  const handleDuplicateDataPackage = useCallback(
    async (values: DuplicateDataPackageFormValues) => {
      if (values.sourceDataPackageId == null) return;
      setIsDuplicating(true);
      try {
        const { data: result } = await duplicateDataPackageMutation({
          variables: {
            arenaId: duplicateTargetArenaId,
            dataPackageId: values.sourceDataPackageId,
            name: values.name || undefined,
            description: values.description || undefined,
          },
        });

        if (result?.finpubDataPackageDuplicate?.success) {
          toast.success("New data package has been created");
          handleCloseDuplicateModal();
          if (duplicateSourceId !== null) {
            handleCloseSlideout();
          }
          await refetchDataPackages();
          return;
        }

        toast.error(
          result?.finpubDataPackageDuplicate?.message ?? "Failed to duplicate data package",
        );
      } catch {
        toast.error("Failed to duplicate data package");
      } finally {
        setIsDuplicating(false);
      }
    },
    [
      duplicateTargetArenaId,
      duplicateDataPackageMutation,
      handleCloseDuplicateModal,
      handleCloseSlideout,
      duplicateSourceId,
      refetchDataPackages,
    ],
  );

  const dataPackageOptions = useMemo(() => {
    return scopedPackages
      .filter((pkg) => pkg.isActive !== false)
      .map((pkg) => ({
        id: pkg.id,
        name: pkg.name,
        arenaId: pkg.arenaId,
      }));
  }, [scopedPackages]);

  const { gridOptions } = useAgGridDefaults({ overrides: GRID_OVERRIDES });
  const { gridOptions: systemGridOptions } = useAgGridDefaults({ overrides: GRID_OVERRIDES });

  const buildColumnDefs = useCallback(
    (isArchivedView: boolean): AgGrid.ColDef<DataPackage>[] => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        flex: 2,
      },
      {
        field: "description",
        headerName: "Description",
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        field: "hierarchy",
        headerName: "Hierarchy",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "formulas",
        headerName: "Formulas",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "queries",
        headerName: "Queries",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "usage",
        headerName: isArchivedView ? "Usage" : "Usage (Active only)",
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: AgGrid.ValueFormatterParams<DataPackage>) =>
          params.value == null ? "-" : String(params.value),
      },
      {
        field: "lastUpdated",
        headerName: isArchivedView ? "Archived by" : "Last updated",
        sortable: true,
        sort: isArchivedView ? undefined : "desc",
        filter: true,
        flex: 2,
        valueGetter: (params) => {
          if (isArchivedView) {
            return getArchivedByCellValue(params.data, formatDateWithTimezone);
          }

          return getLastUpdatedCellValue(params.data, formatDateWithTimezone);
        },
        comparator: (valueA, valueB, nodeA, nodeB) => {
          if (!nodeA?.data || !nodeB?.data) {
            return String(valueA ?? "").localeCompare(String(valueB ?? ""));
          }
          if (isArchivedView) {
            return compareArchivedBy(nodeA.data, nodeB.data);
          }

          return compareDateStrings(nodeA.data.lastUpdated, nodeB.data.lastUpdated);
        },
      },
    ],
    [formatDateWithTimezone],
  );

  const columnDefs = useMemo(
    () => buildColumnDefs(viewFilter === "archived"),
    [buildColumnDefs, viewFilter],
  );
  const systemColumnDefs = useMemo(() => buildColumnDefs(false), [buildColumnDefs]);

  const viewFilterControl = (
    <SegmentedControl
      value={viewFilter}
      onChange={(val) => onViewFilterChange(val as ViewFilter)}
      options={VIEW_FILTER_OPTIONS}
      data-testid="data-packages-view-filter"
    />
  );

  return (
    <Flex flexDirection="column" flexGrow={1} className={styles.dataPackagesContainer}>
      <Flex
        justifyContent="space-between"
        gap={SPACING.SMALL}
        alignItems="center"
        className={styles.contentHeader}
      >
        <Text variant="h3">{title}</Text>
        <Flex gap={SPACING.SMALL} alignItems="center">
          {!isArenaSpecificView && viewFilterControl}
          {!isArenaSpecificView && (
            <DropdownButton label="New data package" align="right" variant="primary">
              <DropdownButton.Action onSelect={handleOpenCreateTakeover}>
                Create new data package
              </DropdownButton.Action>
              <DropdownButton.Action onSelect={handleOpenDuplicateModal}>
                Duplicate from existing
              </DropdownButton.Action>
            </DropdownButton>
          )}
        </Flex>
      </Flex>
      <Flex flexGrow={1}>
        {isArenaSpecificView ? (
          <Flex flexDirection="column" className={styles.tablesWrapper}>
            <div className={styles.tableSection}>
              <Flex
                justifyContent="space-between"
                alignItems="flex-start"
                className={styles.tableSectionHeader}
              >
                <Flex
                  flexDirection="column"
                  gap={SPACING.X_SMALL}
                  className={styles.sectionActionOffset}
                >
                  <Text variant="h4">System</Text>
                </Flex>
                {showGlobalSettingsLink && (
                  <div className={styles.sectionActionOffset}>
                    <Button displayAsLink onClick={handleOpenGlobalSettings}>
                      View global settings
                    </Button>
                  </div>
                )}
              </Flex>
              <div className={styles.sectionActionOffset}>
                <Grid<DataPackage>
                  suppressServerSideFullWidthLoadingRow
                  rowData={loading && !data ? undefined : systemRowData}
                  getRowId={(params) => String(params.data.id)}
                  columnDefs={systemColumnDefs}
                  gridOptions={systemGridOptions}
                  onRowClicked={handleRowClick}
                  noRowsOverlayComponent={NoRowsOverlay}
                  noRowsOverlayComponentParams={{
                    viewFilter: "active" as const,
                    isSystem: true,
                  }}
                />
              </div>
            </div>
            <div className={styles.tableSection}>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                className={styles.tableSectionHeader}
              >
                <Flex
                  flexDirection="column"
                  gap={SPACING.X_SMALL}
                  className={styles.sectionActionOffset}
                >
                  <Text variant="h4">Custom</Text>
                </Flex>
                <Flex
                  gap={SPACING.SMALL}
                  alignItems="center"
                  className={styles.sectionActionOffset}
                >
                  {viewFilterControl}
                  <div>
                    <DropdownButton
                      label="New custom data package"
                      align="right"
                      variant="neutral"
                    >
                      <DropdownButton.Action onSelect={handleOpenCreateTakeover}>
                        Create new data package
                      </DropdownButton.Action>
                      <DropdownButton.Action onSelect={handleOpenDuplicateModal}>
                        Duplicate from existing
                      </DropdownButton.Action>
                    </DropdownButton>
                  </div>
                </Flex>
              </Flex>
              <div className={styles.sectionActionOffset}>
                <Grid<DataPackage>
                  suppressServerSideFullWidthLoadingRow
                  rowData={loading && !data ? undefined : arenaRowData}
                  getRowId={(params) => String(params.data.id)}
                  columnDefs={columnDefs}
                  gridOptions={gridOptions}
                  onRowClicked={handleRowClick}
                  noRowsOverlayComponent={NoRowsOverlay}
                  noRowsOverlayComponentParams={{ viewFilter }}
                />
              </div>
            </div>
          </Flex>
        ) : (
          <Grid<DataPackage>
            suppressServerSideFullWidthLoadingRow
            rowData={loading && !data ? undefined : rowData}
            getRowId={(params) => String(params.data.id)}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            onRowClicked={handleRowClick}
            noRowsOverlayComponent={NoRowsOverlay}
            noRowsOverlayComponentParams={{ viewFilter, isSystem: true }}
          />
        )}
      </Flex>
      <DataPackageSlideout
        isOpen={isSlideoutOpen}
        onClose={handleCloseSlideout}
        arenaId={arenaId}
        arenaName={arenaName}
        dataPackageId={dataPackageId}
        isDefault={selectedPackage?.isDefault ?? false}
        onArchiveSuccess={handleRefreshList}
        onDuplicate={handleDuplicateFromSlideout}
        onDataPackageChanged={handleRefreshList}
      />
      <DuplicateDataPackageModal
        isOpen={isDuplicateModalOpen}
        onClose={handleCloseDuplicateModal}
        onCreate={handleDuplicateDataPackage}
        dataPackages={dataPackageOptions}
        arenaId={arenaId}
        arenaName={arenaName}
        isCreating={isDuplicating}
        defaultSourceDataPackageId={duplicateSourceId}
      />
      <DataPackageTakeover
        isOpen={isNewDataPackage}
        onClose={handleCloseCreateTakeover}
        onSuccess={() => void refetchDataPackages()}
        arenaId={arenaId}
        arenaName={arenaName}
        dataPackages={dataPackageOptions}
      />
    </Flex>
  );
};
